import templatesApi, { type TemplateFilters } from '@/services/templates.service'
import type {
    PrintTemplateCreateModel,
    PrintTemplateModel,
    PrintTemplateUpdateModel
} from '@/types/models/PrintTemplateModel'
import {
    EHandler,
    EPaginationOptions,
    type PaginationDataType,
    type PaginationType
} from '@rfc/rfc-components-library/types'
import { getPageSize } from '@rfc/rfc-components-library/utils'
import { ref, type Ref } from 'vue'
import type { Commit, Dispatch } from 'vuex'
import { ETemplateActions, ETemplateMutations, type TemplateState } from './TemplateStoreTypes'
import { EAlertActions, EStoreGlobalModules } from '@rfc/rfc-components-library/store'
import i18n from '../../locales/i18n'

const state: Ref<PaginationDataType<TemplateState>> = ref({
    data: { templates: [] },

    current_page: 1,
    last_page: 0,
    total: 0,
    to: 0,
    from: 0,
    per_page: getPageSize() ?? EPaginationOptions.DEFAULT,

    loading: false
})

const getters = {
    getTemplates: () => state.value.data?.templates,
    loading: () => state.value.loading
}

const actions = {
    async [ETemplateActions.FETCH_TEMPLATES](
        { commit }: { commit: Commit },
        params?: Partial<PaginationType & TemplateFilters>
    ) {
        commit(ETemplateMutations.SET_LOADING, true)

        return templatesApi
            .getTemplates(params)
            .then(data => {
                commit(ETemplateMutations.SET_TEMPLATES, data.data)
                commit(ETemplateMutations.SET_PAGINATION, data)
                return data
            })
            .finally(() => {
                commit(ETemplateMutations.SET_LOADING, false)
            })
    },

    async [ETemplateActions.CREATE_TEMPLATE](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        data: PrintTemplateCreateModel
    ) {
        commit(ETemplateMutations.SET_LOADING, true)
        return templatesApi
            .createTemplate(data)
            .then(res => {
                commit(ETemplateMutations.ADD_TEMPLATE, res)
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: data.name,
                    key: ETemplateActions.CREATE_TEMPLATE
                })
                return res.data
            })
            .finally(() => commit(ETemplateMutations.SET_LOADING, false))
    },

    async [ETemplateActions.UPDATE_TEMPLATE](
        { commit }: { commit: Commit },
        { id, data }: { id: number; data: PrintTemplateUpdateModel }
    ) {
        commit(ETemplateMutations.SET_LOADING, true)
        const res = await templatesApi
            .updateTemplate(id, data)
            .finally(() => commit(ETemplateMutations.SET_LOADING, false))
        commit(ETemplateMutations.UPDATE_TEMPLATE, res)
        return res
    },

    async [ETemplateActions.DOWNLOAD_TEMPLATE](
        { commit }: { commit: Commit },
        { template }: { template: PrintTemplateModel }
    ) {
        commit(ETemplateMutations.SET_LOADING, true)
        const blob = await templatesApi.downloadTemplate(template.id)
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = template.file_name
        link.click()
        URL.revokeObjectURL(url)
        commit(ETemplateMutations.SET_LOADING, false)
    },

    async [ETemplateActions.DELETE_TEMPLATE](
        { commit }: { commit: Commit },
        { id }: { id: number }
    ) {
        commit(ETemplateMutations.SET_LOADING, true)
        await templatesApi
            .deleteTemplate(id)
            .finally(() => commit(ETemplateMutations.SET_LOADING, false))
        commit(ETemplateMutations.REMOVE_TEMPLATE, id)
    },

    [EHandler.HANDLE_SUCCESS](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreGlobalModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.SUCCESS,
                message: i18n.global.t(`customers.api.success.${key}`, { name })
            },
            { root: true }
        )
    }
}

const mutations = {
    [ETemplateMutations.SET_TEMPLATES](_: TemplateState, templates: PrintTemplateModel[]) {
        state.value.data!.templates = templates
    },
    [ETemplateMutations.SET_LOADING](_: TemplateState, loading: boolean) {
        state.value.loading = loading
    },
    [ETemplateMutations.SET_PAGINATION](
        _: TemplateState,
        data: PaginationDataType<PrintTemplateModel>
    ) {
        state.value.current_page = data.current_page
        state.value.total = data.total
        state.value.last_page = data.last_page
        state.value.to = data.to
        state.value.from = data.from
    },
    [ETemplateMutations.ADD_TEMPLATE](_: TemplateState, template: PrintTemplateModel) {
        state.value.data!.templates = [...(state.value.data?.templates ?? []), template]
    },
    [ETemplateMutations.UPDATE_TEMPLATE](_: TemplateState, updated: PrintTemplateModel) {
        state.value.data!.templates =
            state.value.data?.templates.map(t => (t.id === updated.id ? updated : t)) ?? []
    },
    [ETemplateMutations.REMOVE_TEMPLATE](_: TemplateState, id: number) {
        state.value.data!.templates = state.value.data?.templates.filter(t => t.id !== id) ?? []
    }
}

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters
}
